// modules/comparadorPlataformas/pages/ComparadorPlataformasPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, Autocomplete, TextField, MenuItem, Button,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";

// Puerto del viejo "Comparador Multiplataforma" (PHP): mismas fuentes (Flespi, Flespi2,
// Wialon, LeaseControl, Trsrastreo.com, Vehículos BO, sub-cuentas Flespi personalizadas),
// ahora resuelto en un solo request al backend en vez de que la vista arme varias llamadas.
// El "Estado" del PHP viejo dependía de un endpoint (/api/flespi/estado) que ya no existe
// en este backend. La columna "Estado (equipo_modem)" es distinta: no viene de ninguna
// plataforma externa, sino de la BD local (equipo_modem.estado), resuelta en el backend
// por num_sim/emai — ver comparadorPlataformasService.resolverEstadosDeModems.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

const formatFecha = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};


export default function ComparadorPlataformasPage() {
  const [plataformasDisponibles, setPlataformasDisponibles] = useState([]);
  const [cargandoPlataformas, setCargandoPlataformas] = useState(true);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [base, setBase] = useState("");

  const [resultado, setResultado] = useState(null);
  const [comparando, setComparando] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    axios.get("/api/comparador-plataformas/plataformas")
      .then(({ data }) => setPlataformasDisponibles(Array.isArray(data) ? data : []))
      .catch(() => setPlataformasDisponibles([]))
      .finally(() => setCargandoPlataformas(false));
  }, []);

  const opcionesBase = seleccionadas;
  const labelDe = (valor) => plataformasDisponibles.find((p) => p.value === valor)?.label || valor;

  const handleComparar = async () => {
    if (!seleccionadas.length) return;
    const baseFinal = seleccionadas.includes(base) ? base : seleccionadas[0];
    setComparando(true);
    setError("");
    setPage(0);
    try {
      const { data } = await axios.get("/api/comparador-plataformas", {
        params: { plataformas: seleccionadas.join(","), base: baseFinal },
      });
      setResultado(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo comparar las plataformas.");
      setResultado(null);
    } finally {
      setComparando(false);
    }
  };

  const filasPagina = useMemo(() => {
    if (!resultado?.filas) return [];
    const start = page * rowsPerPage;
    return resultado.filas.slice(start, start + rowsPerPage);
  }, [resultado, page, rowsPerPage]);

  const handleExportarExcel = () => {
    if (!resultado?.filas?.length) return;
    const columnas = resultado.plataformas;
    const encabezado = ["Modem", "Estado (equipo_modem)", ...columnas.flatMap((p) => [`${labelDe(p)} Alias`, `${labelDe(p)} Fecha`]), "Existe en otras"];
    const filas = resultado.filas.map((f) => [
      f.modem,
      f.estado_equipo || "-",
      ...columnas.flatMap((p) => [f.valores[p]?.alias || "-", formatFecha(f.valores[p]?.fecha)]),
      f.existe_en_otras ? "Sí" : "No",
    ]);
    const hoja = XLSX.utils.aoa_to_sheet([encabezado, ...filas]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Comparación");
    XLSX.writeFile(libro, "comparacion_multiplataforma.xlsx");
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Comparador de Plataformas"
        subtitle="Compara los modems registrados en cada plataforma de rastreo (Flespi, Wialon, LeaseControl, etc.)"
        backTo="/dashboard"
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-end" }}>
            <Autocomplete
              multiple
              size="small"
              sx={{ flex: 2, minWidth: 280 }}
              loading={cargandoPlataformas}
              options={plataformasDisponibles}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={plataformasDisponibles.filter((p) => seleccionadas.includes(p.value))}
              onChange={(_, nuevoValor) => {
                const valores = nuevoValor.map((o) => o.value);
                setSeleccionadas(valores);
                if (!valores.includes(base)) setBase(valores[0] || "");
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.value} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Plataformas (en orden de selección)"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cargandoPlataformas && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              select
              size="small"
              label="Base de comparación"
              value={opcionesBase.includes(base) ? base : ""}
              onChange={(e) => setBase(e.target.value)}
              disabled={!seleccionadas.length}
              sx={{ minWidth: 200 }}
            >
              {opcionesBase.map((valor) => (
                <MenuItem key={valor} value={valor}>{labelDe(valor)}</MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              startIcon={comparando ? <CircularProgress size={16} color="inherit" /> : <CompareArrowsIcon />}
              onClick={handleComparar}
              disabled={!seleccionadas.length || comparando}
              sx={{ minHeight: 40 }}
            >
              Comparar
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {resultado && (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {resultado.total} modem(s) en {labelDe(resultado.base)}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleExportarExcel}>
                Exportar Excel
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Modem</TableCell>
                    <TableCell sx={headCellSx}>Estado (equipo_modem)</TableCell>
                    {resultado.plataformas.map((p) => (
                      <TableCell key={p} colSpan={2} sx={headCellSx}>{labelDe(p)}</TableCell>
                    ))}
                    <TableCell sx={headCellSx}>¿Existe en otras?</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!filasPagina.length ? (
                    <TableRow>
                      <TableCell colSpan={resultado.plataformas.length * 2 + 3}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 2 }}>
                          No se encontraron modems.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filasPagina.map((fila) => (
                      <TableRow key={fila.modem} hover>
                        <TableCell>{fila.modem}</TableCell>
                        <TableCell>
                          {fila.estado_equipo ? (
                            <Chip size="small" label={fila.estado_equipo} variant="outlined" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        {resultado.plataformas.map((p) => (
                          <React.Fragment key={p}>
                            <TableCell>{fila.valores[p]?.alias || "-"}</TableCell>
                            <TableCell>{formatFecha(fila.valores[p]?.fecha)}</TableCell>
                          </React.Fragment>
                        ))}
                        <TableCell>
                          <Chip
                            size="small"
                            label={fila.existe_en_otras ? "Sí" : "No"}
                            color={fila.existe_en_otras ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={resultado.filas.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 250]}
              labelRowsPerPage="Por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}
